import format from "string-format";

import { Nullable, NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import {
    BooleanPropertyDefinition,
    DateTimeOffsetPropertyDefinition,
    DateTimePropertyDefinition,
    DecimalPropertyDefinition,
    IMemberDefinition,
    IntegerPropertyDefinition,
    JsonPropertyDefinition,
    LongPropertyDefinition,
    PropertyDefinition,
    RelationDefinition,
    StringPropertyDefinition,
} from "../contracts/base";
import DataType from "../contracts/base/data-type";
import ErrorMessages from "../error-messages";
import Guard from "../guard";
import { MemberDefinitionResource } from "../models/member-definition-resource";
import {
    BooleanPropertyDefinitionResource,
    DateTimeOffsetPropertyDefinitionResource,
    DateTimePropertyDefinitionResource,
    DecimalPropertyDefinitionResource,
    IntegerPropertyDefinitionResource,
    JsonPropertyDefinitionResource,
    LongPropertyDefinitionResource,
    PropertyDefinitionResource,
    StringPropertyDefinitionResource,
} from "../models/property-definition-resource";
import { RelationDefinitionResource } from "../models/relation-definition-resource";
import { MemberConditionMapper } from "./member-condition-mapper";
import { RelationCardinalityMapper } from "./relation-cardinality-mapper";
import { RelationRoleMapper } from "./relation-role-mapper";

export class MemberDefinitionMapper {
    private readonly _client: IExtendedContentHubClient;
    private readonly _memberConditionMapper: MemberConditionMapper;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client, "client");

        this._client = client;
        this._memberConditionMapper = new MemberConditionMapper(client);
    }

    //#region To SDK
    async mapMemberDefinitionsAsync(
        memberDefinitionResources: Array<MemberDefinitionResource>
    ): Promise<Array<IMemberDefinition>> {
        if (!memberDefinitionResources || memberDefinitionResources.length === 0) return [];

        const list: Array<IMemberDefinition> = [];

        for (const memberDefinitionResource of memberDefinitionResources) {
            const memberDefinition = await this.mapMemberDefinitionAsync(memberDefinitionResource);
            if (memberDefinition != null) {
                list.push(memberDefinition);
            }
        }

        return list;
    }

    async mapMemberDefinitionAsync(resource: MemberDefinitionResource): NullableResultPromise<IMemberDefinition> {
        if (!resource) return null;

        let definition: IMemberDefinition;
        if (resource instanceof PropertyDefinitionResource) {
            definition = await this.mapPropertyDefinitionAsync(resource);
        } else if (resource instanceof RelationDefinitionResource) {
            definition = await this.mapRelationDefinitionAsync(resource);
        } else {
            throw Error("Unknown property definition type.");
        }

        definition.conditions = await this._memberConditionMapper.mapMemberConditionsAsync(resource.conditions);
        definition.isSecured = resource.isConditional != null ? resource.isSecured : false;
        definition.isSystemOwned = resource.isSystemOwned;
        definition.allowUpdates = resource.allowUpdates != null ? resource.allowUpdates : false;
        definition.labels = JSON.parse(JSON.stringify(resource.labels));
        definition.helpText = JSON.parse(JSON.stringify(resource.helpText));
        definition.canTriggerConditionalMembers = resource.canTriggerConditionalMembers;
        definition.canWrite = resource.canWrite;

        return definition;
    }

    async mapPropertyDefinitionAsync(resource: PropertyDefinitionResource): Promise<PropertyDefinition> {
        Guard.notNullOrUndefined(resource);

        const initObj: Partial<PropertyDefinition> = {
            boost: resource.boost ?? false,
            includeInCompletion: resource.includeInCompletion ?? false,
            includeInContent: resource.includeInContent ?? false,
            indexed: resource.indexed ?? false,
            isMultiLanguage: resource.isMultiLanguage ?? false,
            isMandatory: resource.isMandatory ?? false,
            isMultiValue: resource.isMultiValue ?? false,
            isUnique: resource.isUnique ?? false,
            canTriggerConditionalMembers: resource.canTriggerConditionalMembers ?? false,
            canWrite: resource.canWrite ?? false,
            ignoreOnExport: resource.ignoreOnExport ?? false,
            storedInGraph: resource.storedInGraph ?? false,
            conditionalValidation: resource.conditionalValidation,
        };

        switch (resource.type) {
            case DataType.Boolean:
                return new BooleanPropertyDefinition(resource.name, { ...initObj });
            case DataType.DateTime:
                return new DateTimePropertyDefinition(resource.name, { ...initObj });
            case DataType.DateTimeOffset:
                return new DateTimeOffsetPropertyDefinition(resource.name, { ...initObj });
            case DataType.Decimal:
                return new DecimalPropertyDefinition(resource.name, { ...initObj });
            case DataType.Integer:
                return new IntegerPropertyDefinition(resource.name, { ...initObj });
            case DataType.Json: {
                const jsonPropResource = resource as JsonPropertyDefinitionResource;
                return new JsonPropertyDefinition(resource.name, {
                    ...initObj,
                    contentType: jsonPropResource.contentType,
                });
            }
            case DataType.Long:
                return new LongPropertyDefinition(resource.name, { ...initObj });
            case DataType.String: {
                const stringPropResource = resource as StringPropertyDefinitionResource;

                const dataSourceName =
                    stringPropResource.dataSource &&
                    (await this._client.linkHelper.nameFromDataSourceAsync(stringPropResource.dataSource));

                return new StringPropertyDefinition(resource.name, {
                    ...initObj,
                    contentType: stringPropResource.contentType,
                    validationExpression: stringPropResource.validationExpression,
                    dataSourceName: dataSourceName,
                });
            }
            default:
                throw format(ErrorMessages.UnsupportedMemberDefinition, resource.name, DataType[resource.type]);
        }
    }

    async mapRelationDefinitionAsync(resource: RelationDefinitionResource): Promise<RelationDefinition> {
        Guard.notNullOrUndefined(resource);
        Guard.notNullOrUndefined(resource.role, "role");
        Guard.notNullOrUndefined(resource.cardinality, "cardinality");

        const associatedEntityDefinitionName = await this._client.linkHelper.nameFromDefinitionAsync(
            resource.associatedEntityDefinition
        );

        const relationDefinition = new RelationDefinition(resource.name, {
            allowNavigation: resource.allowNavigation,
            associatedEntityDefinitionName:
                associatedEntityDefinitionName != null ? associatedEntityDefinitionName : undefined,
            pathHierarchyScore: resource.pathHierarchyScore,
            childIsMandatory: resource.childIsMandatory != null ? resource.childIsMandatory : false,
            parentIsMandatory: resource.parentIsMandatory != null ? resource.parentIsMandatory : false,
            completionIsCopied: resource.completionIsCopied,
            contentIsCopied: resource.contentIsCopied,
            inheritsSecurity: resource.inheritsSecurity,
            isNested: resource.isNested,
            isPathHierarchyRelation: resource.isPathHierarchyRelation,
            isPathRelation: resource.isPathRelation,
            isRenditionRelation: resource.isRenditionRelation,
            isTaxonomyHierarchyRelation: resource.isTaxonomyHierarchyRelation,
            isTaxonomyRelation: resource.isTaxonomyRelation,
            associatedLabels: JSON.parse(JSON.stringify(resource.associatedLabels)),
            nestedPermissions: resource.nestedPermissions,
            cardinality: RelationCardinalityMapper.mapToEnumValue(resource.cardinality),
            role: RelationRoleMapper.mapToEnumValue(resource.role),
            childConditionalValidation: resource.childConditionalValidation,
            parentConditionalValidation: resource.parentConditionalValidation,
        });

        if (resource.nestedProperties != null) {
            relationDefinition.nestedProperties.push(...resource.nestedProperties);
        }

        return relationDefinition;
    }
    //#endregion

    //#region To API resource
    async mapMemberDefinitionResourcesAsync(
        memberDefinitions: Array<IMemberDefinition>
    ): Promise<Array<MemberDefinitionResource>> {
        if (memberDefinitions == null || memberDefinitions.length === 0) return [];

        const list: Array<MemberDefinitionResource> = [];

        for (const memberDefinition of memberDefinitions) {
            const resource = await this.mapMemberDefinitionResourceAsync(memberDefinition);
            if (resource) {
                list.push(resource);
            }
        }

        return list;
    }

    async mapMemberDefinitionResourceAsync(
        memberDefinition: IMemberDefinition
    ): NullableResultPromise<MemberDefinitionResource> {
        if (!memberDefinition) return null;

        let resource: Nullable<MemberDefinitionResource>;

        if (memberDefinition instanceof PropertyDefinition) {
            resource = await this.mapPropertyDefinitionResourceAsync(memberDefinition);
        } else if (memberDefinition instanceof RelationDefinition) {
            resource = await this.mapRelationDefinitionResourceAsync(memberDefinition);
        } else {
            throw format(
                ErrorMessages.UnsupportedMemberDefinition,
                memberDefinition.name,
                DataType[memberDefinition.type]
            );
        }

        if (!resource) return null;

        resource.helpText = JSON.parse(JSON.stringify(memberDefinition.helpText));
        resource.labels = JSON.parse(JSON.stringify(memberDefinition.labels));
        resource.conditions = await this._memberConditionMapper.mapMemberConditionResourcesAsync(
            memberDefinition.conditions
        );
        resource.isConditional = memberDefinition.isConditional;
        resource.isSecured = memberDefinition.isSecured;
        resource.isSystemOwned = memberDefinition.isSystemOwned;
        resource.name = memberDefinition.name;
        resource.allowUpdates = memberDefinition.allowUpdates;
        resource.canTriggerConditionalMembers = memberDefinition.canTriggerConditionalMembers;
        resource.canWrite = memberDefinition.canWrite;

        return resource;
    }

    async mapPropertyDefinitionResourceAsync(
        definition: PropertyDefinition
    ): NullableResultPromise<PropertyDefinitionResource> {
        if (!definition) return null;

        let resource: PropertyDefinitionResource;

        const initObj: Partial<PropertyDefinitionResource> = {
            type: definition.type,
            name: definition.name,
            boost: definition.boost != null ? definition.boost : false,
            includeInCompletion: definition.includeInCompletion,
            includeInContent: definition.includeInContent,
            indexed: definition.indexed,
            isMandatory: definition.isMandatory,
            isUnique: definition.isUnique,
            isMultiLanguage: definition.isMultiLanguage,
            isMultiValue: definition.isMultiValue,
            canTriggerConditionalMembers: definition.canTriggerConditionalMembers ?? false,
            canWrite: definition.canWrite ?? false,
            ignoreOnExport: definition.ignoreOnExport ?? false,
            storedInGraph: definition.storedInGraph ?? false,
            conditionalValidation: definition.conditionalValidation,
        };

        switch (definition.type) {
            case DataType.Boolean:
                resource = new BooleanPropertyDefinitionResource({ ...initObj });
                break;
            case DataType.DateTime:
                resource = new DateTimePropertyDefinitionResource({ ...initObj });
                break;
            case DataType.DateTimeOffset:
                resource = new DateTimeOffsetPropertyDefinitionResource({ ...initObj });
                break;
            case DataType.Decimal:
                resource = new DecimalPropertyDefinitionResource({ ...initObj });
                break;
            case DataType.Integer:
                resource = new IntegerPropertyDefinitionResource({ ...initObj });
                break;
            case DataType.Json: {
                const jsonPropDefinition = definition as JsonPropertyDefinition;
                resource = new JsonPropertyDefinitionResource({
                    ...initObj,
                    contentType: jsonPropDefinition.contentType,
                });
                break;
            }
            case DataType.Long:
                resource = new LongPropertyDefinitionResource({ ...initObj });
                break;
            case DataType.String: {
                const stringPropDefinition = definition as StringPropertyDefinition;

                const dataSourceLink =
                    stringPropDefinition.dataSourceName &&
                    (await this._client.linkHelper.dataSourceToLinkAsync(stringPropDefinition.dataSourceName));

                resource = new StringPropertyDefinitionResource({
                    ...initObj,
                    contentType: stringPropDefinition.contentType,
                    validationExpression: stringPropDefinition.validationExpression,
                    dataSource: dataSourceLink || null,
                });
                break;
            }
            default:
                throw format(ErrorMessages.UnsupportedMemberDefinition, definition.name, DataType[definition.type]);
        }

        return resource;
    }

    async mapRelationDefinitionResourceAsync(
        definition: RelationDefinition
    ): NullableResultPromise<RelationDefinitionResource> {
        if (!definition) return null;

        const associatedEntityDefinitionLink = await this._client.linkHelper.definitionToLinkAsync(
            definition.associatedEntityDefinitionName
        );

        return new RelationDefinitionResource({
            associatedEntityDefinition: associatedEntityDefinitionLink,
            allowNavigation: definition.allowNavigation,
            associatedLabels: JSON.parse(JSON.stringify(definition.associatedLabels)),
            pathHierarchyScore: definition.pathHierarchyScore,
            childIsMandatory: definition.childIsMandatory,
            completionIsCopied: definition.completionIsCopied,
            contentIsCopied: definition.contentIsCopied,
            inheritsSecurity: definition.inheritsSecurity,
            isNested: definition.isNested,
            isPathHierarchyRelation: definition.isPathHierarchyRelation,
            isPathRelation: definition.isPathRelation,
            isRenditionRelation: definition.isRenditionRelation,
            isTaxonomyHierarchyRelation: definition.isTaxonomyHierarchyRelation,
            isTaxonomyRelation: definition.isTaxonomyRelation,
            nestedPermissions: definition.nestedPermissions,
            nestedProperties: JSON.parse(JSON.stringify(definition.nestedProperties)),
            parentIsMandatory: definition.parentIsMandatory,
            cardinality: RelationCardinalityMapper.mapToType(definition.cardinality),
            role: RelationRoleMapper.mapToType(definition.role),
            childConditionalValidation: definition.childConditionalValidation,
            parentConditionalValidation: definition.parentConditionalValidation,
        });
    }
    //#endregion
}
